import React from 'react';
import Content from '../components/content';
import Image from '../components/image';
import Footer from '../components/footer';
import Layout from '../components/layout';
import CaseHeader from '../components/case-header';

const Typeform = () => (
    <Layout>
        <CaseHeader
            title="Typeform"
            role="Product Design"
            time="2017&ndash;2019"
            imgSrc={require('../assets/images/typeform/header-1.jpg')}
            alt="Designstudio 2019 Typeform's rebranding visuals"
            description="Typeform is an online form builder that helps users build conversational and visually captivating forms."
            quote="Obsessing over things that most people don’t notice is what creates a great user experience. We spend time with things like nailing transition screens and alignments between visual elements."
            quoteAuthor="David Okuniev"
            quoteAuthorRole="Founder of Typeform"
        />
        <section>
            <Content kind="p" tag="h2">
                Intro
            </Content>
            <Content>
                In 2017, Typeform was in the middle of a full{' '}
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.typeform.com/blog/inside-story/typeform-version-2/"
                >
                    platform redesign
                </a>
                . Teams needed to validate early assumptions by running user tests with static
                mockups, which proved inefficient when exploring certain interactions.
            </Content>
        </section>
        <section>
            <Content kind="p" tag="h2">
                Design Technologist
            </Content>
            <Content>
                During that exploratory phase, I worked with product designers and PMs from
                different teams to build HTML prototypes for user testing. That gave
                us clearer insight into how to improve certain flows and fix navigation issues while
                watching users interact with something close to the real product.
            </Content>
            <Content>
                This prototype work had to be agile, and creating fake, dynamic content was the
                most time-consuming part. I built a{' '}
                <a href="https://blog.greenroots.info/faking-is-good-back-end-data-and-api-prototyping-with-fakerjs-and-json-server">
                    custom API
                </a>{' '}
                that served random content. Fetching data from it returned a JSON file with
                names, dates, question titles, or images on demand. That sped up the build
                process and helped teams catch edge cases such as long titles, long names, or different
                date formats.
            </Content>
            <Image
                alt="JSON file sample (left) with dynamically generated content. The prototype (right) filled with the new content ready to be used in user test sessions."
                src={require('../assets/images/typeform/fake-data-json.jpg')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                Sampler Design System
            </Content>
            <Content>
                When the new version shipped, I joined the team dedicated to maintaining and
                optimizing Typeform’s forms as a product designer. Over the previous four years, form
                components and new features had been stacked on top of each other, which helped ship
                quickly but led to inconsistent UI, poor responsiveness, and performance
                issues. The company’s long-term vision included new features, a new set of question
                types, and tools for design customization.
            </Content>
            <Content>
                Together with the design system team, led by <a href="https://hayk.design/#/">Hayk An</a>,
                we built{' '}
                <a href="https://typeform.design/sampler/?path=/story/docs-welcome--page">
                    Sampler
                </a>
                , the design system for Typeform’s forms, to address existing issues and support
                upcoming features.
            </Content>
            <Content>
                I ran an audit and proposed a new{' '}
                <a href="https://typeform.design/sampler/?path=/story/form-elements-text--examples">
                    typography scale
                </a>{' '}
                that would cover all UI elements and respond to different screen sizes. We
                also defined a new layout structure: grid, margins, spacing, and responsive logic.
            </Content>
            <Image
                alt="The Sampler typographic scale (still in use)."
                src={require('../assets/images/typeform/typographic-scale-example-sampler.jpg')}
            />
            <Image
                alt="Pairing UI elements to the new scale"
                src={require('../assets/images/typeform/small-breakpoint-typography.jpg')}
            />
            <Content>
                With those foundations in place, we built core form elements, such as{' '}
                <a href="https://typeform.design/sampler/?path=/story/form-elements-button--default-story&amp;args=contentColor:!hex(ffffff);color:!hex(61a97c)">
                    Buttons
                </a>{' '}
                and{' '}
                <a href="https://typeform.design/sampler/?path=/story/form-elements-input--example">
                    Inputs
                </a>
                , and combined them to create others, such as the{' '}
                <a href="https://typeform.design/sampler/?path=/story/typeform-specific-question-header--default">
                    Question header
                </a>{' '}
                and{' '}
                <a href="https://typeform.design/sampler/?path=/story/typeform-specific-screen-screen-layouts--stack-layout">
                    Screen
                </a>{' '}
                components.
            </Content>
            <Content>
                For example, Multiple Choice and Dropdown components are composed of Text and
                Button components. These elements adapt their color scheme based on the
                customer’s color choices to ensure sufficient contrast.
            </Content>
            <Image
                alt="The Question component using Text and RadioButton components."
                src={require('../assets/images/typeform/screen-component-sampler.jpg')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                The Builder
            </Content>
            <Content>
                After we had set up the foundations of the Sampler design system, I was part of the
                team that owned the full Typeform form-creation experience. The goal was to
                improve activation through new functionality. Data and user interviews
                showed that typeforms with custom images, backgrounds, or on-brand colors had
                better engagement and completion rates, which led to better activation.
            </Content>
            <Content>
                We improved the existing design tools, rebuilt the Design theme panel
                from scratch, and added new tools and features—including emoji support, a larger
                set of fonts and themes, and support for custom fonts.
            </Content>
            <Image
                alt="Old design panel (left) vs the new panel (right) that provides a better overview of custom backgrounds, colors, and fonts."
                src={require('../assets/images/typeform/design-theme-panel.png')}
            />
            <Content>
                One of those features was embedding the{' '}
                <a href="https://www.typeform.com/blog/news/unsplash-integration/">
                    Unsplash library
                </a>
                into the builder, giving access to thousands of free, high-quality images. That
                led to more custom images being used in forms. Activation (MAU)
                increased by 3% and retention by 2% in the first 30 days after
                release.
            </Content>
            <Image
                alt="Using the Unsplash gallery for custom backgrounds and images"
                src={require('../assets/images/typeform/unsplash-integration.png')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                Manage access
            </Content>
            <Content>
                Many of our customers (event planners, recruiters, researchers, and growth
                marketers) needed a way to manage the availability of their forms, automate their
                processes, meet deadlines, and avoid problems like overbooking.
            </Content>
            <Content>
                We interviewed around 30 customers, which helped us identify known issues and key use
                cases. We then wrote a problem statement and manifesto describing why and how we
                would solve the problem, how we’d measure success, and how we’d phase the work.
            </Content>
            <Content>
                We built an auto-close feature with custom logic, such as setting a maximum number of
                responses or a time/date limit. We rolled out the feature gradually in several
                phases: an initial beta focused on adoption among existing customers, an A/B test
                focused on adoption and new signups, and finally a full release to all
                free and PRO users. That drove a 2.8% increase in MAU adoption among new and existing users in the
                first 30 days of the A/B test.
            </Content>
            <Image
                alt="Part of the marketing campaigns to create custom screens once the form is closed."
                src={require('../assets/images/typeform/manage-access-typeform.gif')}
            />
            <Image
                alt="One of several design iterations with manual control and basic custom screen options for PRO users."
                src={require('../assets/images/typeform/manage-acess-first.png')}
            />
        </section>
        <Footer>
            <a href="./new-relic">
                <img
                    loading="lazy"
                    src={require('../assets/images/new-relic/header-3.gif')}
                    alt={'New Relic use case'}
                />
            </a>
            <a href="./playfulbet">
                <img
                    loading="lazy"
                    src={require('../assets/images/playfulbet/header.gif')}
                    alt={'Playfulbet use case'}
                />
            </a>
        </Footer>
    </Layout>
);

export default Typeform;
